import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/storefront/PageHeader";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useBasket } from "@/context/BasketContext";
import { useSite } from "@/context/SiteContext";
import { imgUrl, money } from "@/lib/api";

export default function Basket() {
  const { items, updateQuantity, remove, subtotal, itemKey } = useBasket();
  const { storeOpen, settings } = useSite();
  const navigate = useNavigate();
  const delivery = settings?.delivery || {};
  const fee = delivery.free_delivery_enabled !== false && Number(delivery.free_over) > 0 && subtotal >= Number(delivery.free_over) ? 0 : Number(delivery.fee || 0);
  const minOrder = Number(delivery.min_order) || 0;
  const belowMin = minOrder > 0 && subtotal < minOrder;

  return (
    <div data-testid="basket-page">
      <Seo title="Your Basket | Eileen Jenkins Florist" />
      <PageHeader title="Your Basket" testId="basket-header" />
      <div className="max-w-4xl mx-auto px-4 pb-20">
        {items.length === 0 ? (
          <div className="text-center py-16" data-testid="basket-empty">
            <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground/40" />
            <p className="font-display text-2xl mt-4">Your basket is empty</p>
            <p className="text-muted-foreground mt-2">Browse our flowers and add something beautiful.</p>
            <Button asChild className="mt-6 rounded-full" data-testid="basket-continue-shopping-btn"><Link to="/shop">Continue shopping</Link></Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-10">
            <div className="md:col-span-2 space-y-4" data-testid="basket-items">
              {items.map((it) => {
                const key = itemKey(it);
                const line = (it.unit_price + (it.addons || []).reduce((s, a) => s + a.price, 0)) * it.quantity;
                return (
                  <div key={key} className="flex gap-4 border rounded-xl p-4 bg-card" data-testid={`basket-item-${it.product_id}`}>
                    <Link to={`/product/${it.slug}`} className="shrink-0">
                      {it.image ? (
                        <img src={imgUrl(it.image)} alt={it.name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg" />
                      ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-lg" />
                      )}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{it.name}</p>
                      {it.variation && <p className="text-sm text-muted-foreground">{it.variation}</p>}
                      {(it.addons || []).map((a) => (
                        <p key={a.name} className="text-xs text-muted-foreground">+ {a.name}{a.price > 0 ? ` (${money(a.price)})` : ""}</p>
                      ))}
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center border rounded-full">
                          <button onClick={() => updateQuantity(key, it.quantity - 1)} className="p-2" data-testid="basket-qty-decrease" aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
                          <span className="w-7 text-center text-sm font-medium" data-testid="basket-qty-value">{it.quantity}</span>
                          <button onClick={() => updateQuantity(key, it.quantity + 1)} className="p-2" data-testid="basket-qty-increase" aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <button onClick={() => remove(key)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid="basket-remove-btn" aria-label="Remove">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="font-semibold whitespace-nowrap">{money(line)}</p>
                  </div>
                );
              })}
              <Button variant="ghost" asChild className="rounded-full" data-testid="continue-shopping-btn">
                <Link to="/shop">← Continue shopping</Link>
              </Button>
            </div>

            <div className="border rounded-xl p-6 bg-card h-fit" data-testid="basket-summary">
              <h2 className="font-display text-xl mb-4">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="basket-subtotal">{money(subtotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span data-testid="basket-delivery">{fee === 0 ? "Free" : money(fee)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
                  <span>Total</span><span data-testid="basket-total">{money(subtotal + fee)}</span>
                </div>
              </div>
              {!storeOpen && (
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-4" data-testid="basket-store-closed">
                  {settings?.closed_message || "Online ordering is currently closed."}
                </p>
              )}
              {storeOpen && belowMin && (
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-4" data-testid="basket-min-order-note">
                  Minimum online order is {money(minOrder)} — add {money(minOrder - subtotal)} more to check out.
                </p>
              )}
              <Button
                className="w-full rounded-full mt-6"
                size="lg"
                disabled={!storeOpen || belowMin}
                onClick={() => navigate("/checkout")}
                data-testid="checkout-btn"
              >
                Proceed to checkout
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
